import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  LineChart, Line, CartesianGrid, XAxis, YAxis,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

import { isDemoBrand, demoHash } from '../../utils/demoBrand';

const API = 'https://api.glowytics.com';

// Static RTB House datasets for demo brands (raw API shape → normalize() digests it).
function buildDemoRtbDaily() {
  const today = new Date();
  const rows = [];
  for (let i = 0; i < 90; i++) {
    const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() - i);
    const ds = d.toISOString().slice(0, 10);
    const h = demoHash('rtb' + ds);
    const imps = 80000 + (h % 220000);
    const clicks = Math.round(imps * (0.004 + (h % 12) / 1000));
    const cost = Math.round(imps * (0.9 + (h % 50) / 100) / 100);
    const convs = Math.round(clicks * (0.02 + (h % 20) / 1000));
    const value = Math.round(convs * (500 + (h % 700)));
    rows.push({
      date: ds,
      impsCount: imps,
      clicksCount: clicks,
      campaignCost: cost,
      conversionsCount: convs,
      conversionsValue: value,
      cr: clicks > 0 ? convs / clicks : 0,
      ecpa: convs > 0 ? cost / convs : 0,
      ga4Sessions: Math.round(clicks * (0.7 + (h % 30) / 100)),
      ga4Purchases: Math.round(convs * (0.8 + (h % 30) / 100)),
      ga4Revenue: Math.round(value * (0.9 + (h % 20) / 100)),
    });
  }
  return rows;
}

function buildDemoRtbCampaigns() {
  const names = ['Retargeting - Dynamic', 'Prospecting - Lookalike', 'Cart Abandoners', 'Category - Fashion', 'Upper Funnel - Awareness'];
  return names.map((n, i) => {
    const h = demoHash('rtbc' + n);
    const imps = 400000 + (h % 2000000);
    const clicks = Math.round(imps * (0.004 + (h % 12) / 1000));
    const cost = Math.round(imps * (0.9 + (h % 50) / 100) / 100);
    const convs = Math.round(clicks * (0.02 + (h % 20) / 1000));
    const value = Math.round(convs * (500 + (h % 700)));
    return {
      subcampaign: `demo-sub-${i}`,
      campaignName: n,
      impsCount: imps,
      clicksCount: clicks,
      ctr: imps > 0 ? clicks / imps : 0,
      campaignCost: cost,
      conversionsCount: convs,
      cr: clicks > 0 ? convs / clicks : 0,
      ecpa: convs > 0 ? cost / convs : 0,
      conversionsValue: value,
      ga4Sessions: Math.round(clicks * (0.7 + (h % 30) / 100)),
      ga4Purchases: Math.round(convs * (0.8 + (h % 30) / 100)),
      ga4Revenue: Math.round(value * (0.9 + (h % 20) / 100)),
    };
  });
}

const fmt = (n) => Math.round(Number(n || 0)).toLocaleString('en-US');
const fmt2 = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const Ga4Th = ({ label }) => (
  <th className="text-right px-4 py-3 font-semibold whitespace-nowrap">
    <span className="inline-flex items-center gap-1">
      <img src="/GA4logoicon.svg" alt="GA4" className="w-3.5 h-3.5 inline" /><span className="text-slate-700">{label}</span>
    </span>
  </th>
);
const fmtCompact = (v) => {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return '0';
  return new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(n);
};
const pct = (v) => `${Number(v || 0).toFixed(2)} %`;
const toYmd = (value) => {
  if (!value) return '';
  const m = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) return m[1];
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
};
const ymd = (d) => d.toISOString().slice(0, 10);

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const getISOWeek = (dateStr) => {
  const d = new Date(dateStr + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

const formatBreakdownDate = (dateStr, grain) => {
  if (grain === 'day') return dateStr;
  if (grain === 'week') {
    const wn = getISOWeek(dateStr);
    const y = dateStr.slice(0, 4);
    return `Week ${wn}, ${y}`;
  }
  if (grain === 'month') {
    const parts = dateStr.split('-');
    const mi = parseInt(parts[1], 10) - 1;
    return `${MONTH_NAMES[mi]} ${parts[0]}`;
  }
  return dateStr;
};

const normalize = (json) => {
  const root = Array.isArray(json?.rows) ? json.rows
    : Array.isArray(json?.data?.data) ? json.data.data
    : Array.isArray(json?.data) ? json.data : [];
  return root
    .map((r) => ({
      date: toYmd(r?.date || r?.day || ''),
      imps: Number(r?.impsCount || 0),
      clicks: Number(r?.clicksCount || 0),
      cost: Number(r?.campaignCost || 0),
      convs: Number(r?.conversionsCount || 0),
      value: Number(r?.conversionsValue || 0),
      cr: Number(r?.cr || 0),
      ecpa: Number(r?.ecpa || 0),
      ga4Sessions: Number(r?.ga4Sessions || 0),
      ga4Purchases: Number(r?.ga4Purchases || 0),
      ga4Revenue: Number(r?.ga4Revenue || 0),
    }))
    .filter((r) => !!r.date)
    .sort((a, b) => b.date.localeCompare(a.date));
};

export default function RtbHouseConnect() {
  const [advHash, setAdvHash] = useState('');
  const [period, setPeriod] = useState('30');
  const [cFrom, setCFrom] = useState(() => ymd(new Date(Date.now() - 29 * 86400000)));
  const [cTo, setCTo] = useState(() => ymd(new Date()));
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [error, setError] = useState('');
  const [grain, setGrain] = useState('day');
  const [breakdownData, setBreakdownData] = useState(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const fromRef = useRef(null);
  const prevPeriod = useRef(period);

  const hdrs = useMemo(() => {
    const b = localStorage.getItem('currentBrandId');
    const t = localStorage.getItem('token');
    return {
      ...(b ? { 'X-Brand-ID': b } : {}),
      ...(t ? { Authorization: `Bearer ${t}` } : {}),
      'Content-Type': 'application/json',
    };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/auth/rtb-house/connection`, { credentials: 'include', headers: hdrs });
        if (!r.ok) return;
        const j = await r.json();
        if (j?.hasConnection && j?.connection?.customerId) setAdvHash(String(j.connection.customerId));
      } catch (_) {}
    })();
  }, [hdrs]);

  const dateRange = useMemo(() => {
    if (period === 'all') return {};
    if (period === 'custom') return { dayFrom: cFrom || undefined, dayTo: cTo || undefined };
    const days = Number(period || 30);
    const end = new Date();
    const start = new Date(end.getTime() - (days - 1) * 86400000);
    return { dayFrom: ymd(start), dayTo: ymd(end) };
  }, [period, cFrom, cTo]);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (period === 'custom' && (!cFrom || !cTo || cFrom > cTo)) {
        throw new Error('Select a valid custom date range');
      }
      const qs = new URLSearchParams({
        grain: 'day',
        ...(dateRange.dayFrom ? { dayFrom: dateRange.dayFrom } : {}),
        ...(dateRange.dayTo ? { dayTo: dateRange.dayTo } : {}),
        ...(advHash.trim() ? { advertiserHash: advHash.trim() } : {}),
      });
      const [r, rc] = await Promise.all([
        fetch(`${API}/api/rtbhouse/stats?${qs}`, { credentials: 'include', headers: hdrs }),
        fetch(`${API}/api/rtbhouse/stats/by-campaign?${qs}`, { credentials: 'include', headers: hdrs }),
      ]);
      const j = await r.json();
      if (!r.ok || j?.success === false) throw new Error(j?.error || `HTTP ${r.status}`);
      const demo = isDemoBrand(localStorage.getItem('currentBrandId')) && normalize(j).length === 0;
      setData(demo ? { rows: buildDemoRtbDaily() } : j);
      try {
        const jc = await rc.json();
        const cr = Array.isArray(jc?.rows) ? jc.rows : [];
        setCampaigns((demo && cr.length === 0) ? buildDemoRtbCampaigns() : cr);
      } catch (_) { setCampaigns(demo ? buildDemoRtbCampaigns() : []); }
    } catch (e) {
      if (isDemoBrand(localStorage.getItem('currentBrandId'))) {
        setData({ rows: buildDemoRtbDaily() });
        setCampaigns(buildDemoRtbCampaigns());
        setError('');
      } else {
        setError(e?.message || 'Failed to load');
        setData(null);
        setCampaigns([]);
      }
    } finally {
      setLoading(false);
    }
  }, [period, cFrom, cTo, dateRange, advHash, hdrs]);

  const fetchBreakdown = useCallback(async (g) => {
    setBreakdownLoading(true);
    try {
      const isAll = g !== 'day';
      const qs = new URLSearchParams({
        grain: g,
        ...(isAll ? {} : dateRange.dayFrom ? { dayFrom: dateRange.dayFrom } : {}),
        ...(isAll ? {} : dateRange.dayTo ? { dayTo: dateRange.dayTo } : {}),
        ...(advHash.trim() ? { advertiserHash: advHash.trim() } : {}),
      });
      const r = await fetch(`${API}/api/rtbhouse/stats?${qs}`, { credentials: 'include', headers: hdrs });
      const j = await r.json();
      if (!r.ok || j?.success === false) throw new Error(j?.error || `HTTP ${r.status}`);
      const demo = isDemoBrand(localStorage.getItem('currentBrandId')) && normalize(j).length === 0;
      setBreakdownData(demo ? { rows: buildDemoRtbDaily() } : j);
    } catch (_) {
      setBreakdownData(isDemoBrand(localStorage.getItem('currentBrandId')) ? { rows: buildDemoRtbDaily() } : null);
    } finally {
      setBreakdownLoading(false);
    }
  }, [dateRange, advHash, hdrs]);

  useEffect(() => { fetchStats(); fetchBreakdown(grain); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchBreakdown(grain);
  }, [grain]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (prevPeriod.current !== 'custom' && period === 'custom') {
      setTimeout(() => {
        const el = fromRef.current;
        if (el) { el.focus(); try { el.showPicker(); } catch (_) {} }
      }, 50);
    }
    prevPeriod.current = period;
  }, [period]);

  useEffect(() => {
    if (period !== 'custom' || !cFrom || !cTo || cFrom > cTo) return;
    fetchStats();
    fetchBreakdown(grain);
  }, [cTo]); // eslint-disable-line react-hooks/exhaustive-deps

  const allRows = useMemo(() => normalize(data), [data]);
  const rows = useMemo(() => {
    if (!allRows.length) return [];
    if (period === 'all' || period === 'custom') return allRows;
    const n = Number(period || 30);
    return Number.isFinite(n) && n > 0 ? allRows.slice(0, n) : allRows;
  }, [allRows, period]);

  const breakdownRows = useMemo(() => {
    if (grain === 'day') return rows;
    return normalize(breakdownData);
  }, [grain, rows, breakdownData]);

  const totals = useMemo(() => {
    const t = { imps: 0, clicks: 0, cost: 0, convs: 0, value: 0, ga4Sessions: 0, ga4Purchases: 0, ga4Revenue: 0 };
    rows.forEach((r) => { t.imps += r.imps; t.clicks += r.clicks; t.cost += r.cost; t.convs += r.convs; t.value += r.value; t.ga4Sessions += r.ga4Sessions; t.ga4Purchases += r.ga4Purchases; t.ga4Revenue += r.ga4Revenue; });
    t.ctr = t.imps > 0 ? (t.clicks / t.imps) * 100 : 0;
    t.cr = t.clicks > 0 ? (t.convs / t.clicks) * 100 : 0;
    t.cpc = t.clicks > 0 ? t.cost / t.clicks : 0;
    t.ecpa = t.convs > 0 ? t.cost / t.convs : 0;
    t.roas = t.cost > 0 ? t.ga4Revenue / t.cost : 0;
    return t;
  }, [rows]);

  const chart = useMemo(() => [...rows].reverse().map((r) => ({ date: r.date, Cost: r.cost, Clicks: r.clicks, Conversions: r.convs, 'GA4 Revenue': r.ga4Revenue, 'GA4 Sessions': r.ga4Sessions })), [rows]);

  const rangeLabel = useMemo(() => {
    if (period === 'custom' && cFrom && cTo) return `${cFrom}  —  ${cTo}`;
    if (period === 'all' && allRows.length) return `${allRows[0].date}  —  ${allRows[allRows.length - 1].date}`;
    if (dateRange.dayFrom && dateRange.dayTo) return `${dateRange.dayFrom}  —  ${dateRange.dayTo}`;
    return '';
  }, [period, cFrom, cTo, allRows, dateRange]);

  const exportCsv = () => {
    if (!breakdownRows.length) return;
    const colLabel = grain === 'day' ? 'Date' : grain === 'week' ? 'Week' : 'Month';
    const h = [colLabel, 'Impressions', 'Clicks', 'CTR', 'Cost_TRY', 'CPC_TRY', 'Conversions', 'CR', 'eCPA_TRY', 'ConversionsValue_TRY', 'GA4_Sessions', 'GA4_Transactions', 'GA4_Revenue_TRY'];
    const lines = breakdownRows.map((r) => [
      formatBreakdownDate(r.date, grain), r.imps, r.clicks,
      `${(r.imps > 0 ? (r.clicks / r.imps) * 100 : 0).toFixed(2)}%`,
      r.cost.toFixed(2), (r.clicks > 0 ? r.cost / r.clicks : 0).toFixed(2), r.convs, r.cr.toFixed(4), r.ecpa.toFixed(2), r.value.toFixed(2),
      r.ga4Sessions, r.ga4Purchases, r.ga4Revenue.toFixed(2),
    ].join(','));
    const blob = new Blob([[h.join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `rtb-house-${grain}-${period === 'custom' ? `${cFrom}_${cTo}` : period === 'all' ? 'all' : `last-${period}d`}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const onPeriodChange = (val) => {
    setPeriod(val);
    if (val !== 'custom') setTimeout(() => { fetchStats(); fetchBreakdown(grain); }, 0);
  };

  return (
    <div className="p-6 max-w-[1200px] mx-auto space-y-5">

      {/* ─── HEADER ─── */}
      <div className="rounded-2xl border border-slate-200 bg-white px-6 py-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 flex items-center justify-center shadow-sm">
              <img src="/logo--rtb-houselogo.svg" alt="RTB House" className="h-6 object-contain" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">RTB House Dashboard</h1>
              <p className="text-sm text-slate-500 mt-0.5">Real-time performance overview</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <select
              value={period}
              onChange={(e) => onPeriodChange(e.target.value)}
              className="h-10 px-4 pr-8 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 cursor-pointer appearance-none"
              style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='none' viewBox='0 0 12 12'%3E%3Cpath d='M3 4.5l3 3 3-3' stroke='%2364748b' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center' }}
            >
              <option value="7">Last 7 Days</option>
              <option value="30">Last 30 Days</option>
              <option value="90">Last 90 Days</option>
              <option value="all">All Time</option>
              <option value="custom">Custom Date</option>
            </select>

            {period === 'custom' && (
              <>
                <input ref={fromRef} type="date" value={cFrom} onChange={(e) => setCFrom(e.target.value)}
                  className="h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 w-[140px]" />
                <span className="text-slate-400 text-xs font-medium">—</span>
                <input type="date" value={cTo} onChange={(e) => setCTo(e.target.value)}
                  className="h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 w-[140px]" />
              </>
            )}

            <button onClick={fetchStats} disabled={loading} title="Refresh"
              className="h-10 w-10 rounded-lg border border-slate-200 bg-white flex items-center justify-center text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition">
              <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
        </div>

        {rangeLabel && (
          <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500 font-medium tracking-wide">
            {rangeLabel}
          </div>
        )}
      </div>

      {/* ─── KPI CARDS ─── */}
      {rows.length > 0 && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { label: 'Total Spend', val: `₺${fmt(totals.cost)}`, accent: true },
              { label: 'Impressions', val: fmtCompact(totals.imps) },
              { label: 'Clicks', val: fmt(totals.clicks) },
              { label: 'CPC', val: `₺${fmt2(totals.cpc)}` },
              { label: 'Conversions', val: fmt(totals.convs) },
              { label: 'Conversion Value', val: `₺${fmt(totals.value)}` },
            ].map((c) => (
              <div key={c.label} className={`rounded-xl border border-slate-200 p-4 ${c.accent ? 'bg-indigo-600 text-white' : 'bg-white text-slate-800'}`}>
                <div className={`text-xs font-medium mb-2 ${c.accent ? 'text-indigo-200' : 'text-slate-500'}`}>{c.label}</div>
                <div className="text-2xl font-bold leading-tight">{c.val}</div>
              </div>
            ))}
          </div>

          {/* ─── ROW 2 KPI CARDS ─── */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {[
              { label: 'ROAS', val: totals.roas.toFixed(2), color: 'text-amber-600' },
              { label: 'eCPA', val: `₺${fmt(totals.ecpa)}`, color: 'text-slate-800' },
              { label: 'Sessions', val: fmt(totals.ga4Sessions), color: 'text-blue-600', ga4: true },
              { label: 'Transactions', val: fmt(totals.ga4Purchases), color: 'text-emerald-600', ga4: true },
              { label: 'Transaction Value', val: `₺${fmt(totals.ga4Revenue)}`, color: 'text-green-600', ga4: true },
            ].map((c) => (
              <div key={c.label} className="rounded-xl border border-slate-200 p-4 bg-white">
                <div className="text-xs font-medium mb-2 text-slate-500 flex items-center gap-1">
                  {c.ga4 && <img src="/GA4logoicon.svg" alt="GA4" className="w-3.5 h-3.5" />}{c.label}
                </div>
                <div className={`text-2xl font-bold leading-tight ${c.color}`}>{c.val}</div>
              </div>
            ))}
          </div>

          {/* ─── CHART ─── */}
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <h2 className="text-base font-semibold text-slate-800 mb-4">Performance Trends</h2>
            <div className="h-[340px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chart} margin={{ top: 5, right: 20, bottom: 5, left: 5 }}>
                  <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }}
                    tickFormatter={(v) => { const p = v.split('-'); return `${Number(p[1])}/${Number(p[2])}`; }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={fmt} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={fmt} />
                  <Tooltip
                    contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,.08)' }}
                    formatter={(v, name) => [`${fmt(v)}${name === 'Cost' || name === 'GA4 Revenue' ? ' TRY' : ''}`, name]}
                    labelFormatter={(l) => `Date: ${l}`}
                  />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                  <Line yAxisId="right" type="monotone" dataKey="Cost" stroke="#94a3b8" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                  <Line yAxisId="right" type="monotone" dataKey="GA4 Revenue" stroke="#10b981" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                  <Line yAxisId="left" type="monotone" dataKey="Clicks" stroke="#ef4444" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                  <Line yAxisId="left" type="monotone" dataKey="GA4 Sessions" stroke="#3b82f6" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                  <Line yAxisId="left" type="monotone" dataKey="Conversions" stroke="#7c3aed" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* ─── CAMPAIGN BREAKDOWN ─── */}
          {campaigns.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100">
                <h2 className="text-base font-semibold text-slate-800">Campaign Breakdown</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                      <th className="text-left px-5 py-3 font-semibold">Campaign</th>
                      <th className="text-right px-5 py-3 font-semibold">Imps</th>
                      <th className="text-right px-5 py-3 font-semibold">Clicks</th>
                      <th className="text-right px-5 py-3 font-semibold">CTR</th>
                      <th className="text-right px-5 py-3 font-semibold">Cost (TRY)</th>
                      <th className="text-right px-5 py-3 font-semibold">CPC (TRY)</th>
                      <th className="text-right px-5 py-3 font-semibold">Convs.</th>
                      <th className="text-right px-5 py-3 font-semibold">CR</th>
                      <th className="text-right px-5 py-3 font-semibold">eCPA (TRY)</th>
                      <th className="text-right px-5 py-3 font-semibold">Conv. Val. (TRY)</th>
                      <Ga4Th label="S" />
                      <Ga4Th label="T" />
                      <Ga4Th label="Rev" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {campaigns.map((c) => (
                      <tr key={c.subcampaign} className="hover:bg-slate-50/50 transition">
                        <td className="px-5 py-2.5 text-slate-700 font-medium max-w-[300px] truncate" title={c.campaignName || c.subcampaign}>
                          {c.campaignName || c.subcampaign}
                        </td>
                        <td className="px-5 py-2.5 text-right text-slate-600">{fmt(c.impsCount)}</td>
                        <td className="px-5 py-2.5 text-right text-slate-600">{fmt(c.clicksCount)}</td>
                        <td className="px-5 py-2.5 text-right text-slate-600">{pct(c.ctr * 100)}</td>
                        <td className="px-5 py-2.5 text-right text-slate-600">{fmt(c.campaignCost)}</td>
                        <td className="px-5 py-2.5 text-right text-slate-600">{fmt2(Number(c.clicksCount) > 0 ? Number(c.campaignCost) / Number(c.clicksCount) : 0)}</td>
                        <td className="px-5 py-2.5 text-right text-slate-600">{fmt(c.conversionsCount)}</td>
                        <td className="px-5 py-2.5 text-right text-slate-600">{pct(c.cr * 100)}</td>
                        <td className="px-5 py-2.5 text-right text-slate-600">{fmt(c.ecpa)}</td>
                        <td className="px-5 py-2.5 text-right text-slate-600">{fmt(c.conversionsValue)}</td>
                        <td className="px-5 py-2.5 text-right text-blue-600">{fmt(c.ga4Sessions)}</td>
                        <td className="px-5 py-2.5 text-right text-emerald-600">{fmt(c.ga4Purchases)}</td>
                        <td className="px-5 py-2.5 text-right text-green-600">₺{fmt(c.ga4Revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-5 py-2 text-xs text-slate-400 border-t border-slate-100">{campaigns.length} campaigns</div>
            </div>
          )}

          {/* ─── TABLE ─── */}
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <h2 className="text-base font-semibold text-slate-800">Breakdown</h2>
                <div className="flex rounded-lg border border-slate-200 overflow-hidden text-xs font-medium">
                  {[['day', 'Daily'], ['week', 'Weekly'], ['month', 'Monthly']].map(([g, lbl]) => (
                    <button key={g} onClick={() => setGrain(g)}
                      className={`px-3 py-1.5 transition ${grain === g ? 'bg-red-500 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
                      {lbl}
                    </button>
                  ))}
                </div>
                {breakdownLoading && <span className="text-xs text-slate-400 animate-pulse">Loading...</span>}
              </div>
              <button onClick={exportCsv} disabled={!breakdownRows.length}
                className="px-4 py-1.5 rounded-full bg-red-500 text-white text-xs font-medium disabled:opacity-50 hover:bg-red-600 transition">
                Export to CSV
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                    <th className="text-left px-5 py-3 font-semibold">{grain === 'day' ? 'Date' : grain === 'week' ? 'Week' : 'Month'}</th>
                    <th className="text-right px-5 py-3 font-semibold">Imps</th>
                    <th className="text-right px-5 py-3 font-semibold">Clicks</th>
                    <th className="text-right px-5 py-3 font-semibold">CTR</th>
                    <th className="text-right px-5 py-3 font-semibold">Cost (TRY)</th>
                    <th className="text-right px-5 py-3 font-semibold">CPC (TRY)</th>
                    <th className="text-right px-5 py-3 font-semibold">Convs.</th>
                    <th className="text-right px-5 py-3 font-semibold">CR</th>
                    <th className="text-right px-5 py-3 font-semibold">eCPA (TRY)</th>
                    <th className="text-right px-5 py-3 font-semibold">Conv. Val. (TRY)</th>
                    <Ga4Th label="S" />
                    <Ga4Th label="T" />
                    <Ga4Th label="Rev" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {breakdownRows.map((r) => (
                    <tr key={r.date} className="hover:bg-slate-50/50 transition">
                      <td className="px-5 py-2.5 text-slate-700 font-medium whitespace-nowrap">{formatBreakdownDate(r.date, grain)}</td>
                      <td className="px-5 py-2.5 text-right text-slate-600">{fmt(r.imps)}</td>
                      <td className="px-5 py-2.5 text-right text-slate-600">{fmt(r.clicks)}</td>
                      <td className="px-5 py-2.5 text-right text-slate-600">{pct(r.imps > 0 ? (r.clicks / r.imps) * 100 : 0)}</td>
                      <td className="px-5 py-2.5 text-right text-slate-600">{fmt(r.cost)}</td>
                      <td className="px-5 py-2.5 text-right text-slate-600">{fmt2(r.clicks > 0 ? r.cost / r.clicks : 0)}</td>
                      <td className="px-5 py-2.5 text-right text-slate-600">{fmt(r.convs)}</td>
                      <td className="px-5 py-2.5 text-right text-slate-600">{pct(r.cr * 100)}</td>
                      <td className="px-5 py-2.5 text-right text-slate-600">{fmt(r.ecpa)}</td>
                      <td className="px-5 py-2.5 text-right text-slate-600">{fmt(r.value)}</td>
                      <td className="px-5 py-2.5 text-right text-blue-600">{fmt(r.ga4Sessions)}</td>
                      <td className="px-5 py-2.5 text-right text-emerald-600">{fmt(r.ga4Purchases)}</td>
                      <td className="px-5 py-2.5 text-right text-green-600">₺{fmt(r.ga4Revenue)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  {(() => {
                    const bt = { imps: 0, clicks: 0, cost: 0, convs: 0, value: 0, ga4Sessions: 0, ga4Purchases: 0, ga4Revenue: 0 };
                    breakdownRows.forEach((r) => { bt.imps += r.imps; bt.clicks += r.clicks; bt.cost += r.cost; bt.convs += r.convs; bt.value += r.value; bt.ga4Sessions += r.ga4Sessions; bt.ga4Purchases += r.ga4Purchases; bt.ga4Revenue += r.ga4Revenue; });
                    bt.ctr = bt.imps > 0 ? (bt.clicks / bt.imps) * 100 : 0;
                    bt.cr = bt.clicks > 0 ? (bt.convs / bt.clicks) * 100 : 0;
                    bt.cpc = bt.clicks > 0 ? bt.cost / bt.clicks : 0;
                    bt.ecpa = bt.convs > 0 ? bt.cost / bt.convs : 0;
                    return (
                      <tr className="bg-slate-50 font-bold text-slate-800">
                        <td className="px-5 py-3">Total</td>
                        <td className="px-5 py-3 text-right">{fmt(bt.imps)}</td>
                        <td className="px-5 py-3 text-right">{fmt(bt.clicks)}</td>
                        <td className="px-5 py-3 text-right">{pct(bt.ctr)}</td>
                        <td className="px-5 py-3 text-right">{fmt(bt.cost)}</td>
                        <td className="px-5 py-3 text-right">{fmt2(bt.cpc)}</td>
                        <td className="px-5 py-3 text-right">{fmt(bt.convs)}</td>
                        <td className="px-5 py-3 text-right">{pct(bt.cr)}</td>
                        <td className="px-5 py-3 text-right">{fmt(bt.ecpa)}</td>
                        <td className="px-5 py-3 text-right">{fmt(bt.value)}</td>
                        <td className="px-5 py-3 text-right text-blue-600">{fmt(bt.ga4Sessions)}</td>
                        <td className="px-5 py-3 text-right text-emerald-600">{fmt(bt.ga4Purchases)}</td>
                        <td className="px-5 py-3 text-right text-green-600">₺{fmt(bt.ga4Revenue)}</td>
                      </tr>
                    );
                  })()}
                </tfoot>
              </table>
            </div>
            <div className="px-5 py-2 text-xs text-slate-400 border-t border-slate-100">{breakdownRows.length} rows</div>
          </div>
        </>
      )}

      {!rows.length && !error && !loading && (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
          No data available for the selected period.
        </div>
      )}

      {loading && !rows.length && (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
          Loading...
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm px-5 py-3">
          {error}
        </div>
      )}
    </div>
  );
}
